// utils/utm.ts — capture UTM/click-id params on first landing, persist 60d.
// Used by <LeadForm/> to attach attribution to every lead submit.

const STORAGE_KEY = 'bbk_attribution';
const TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

const TRACKED_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'gclid', 'fbclid', 'ttclid', 'msclkid'
] as const;

const TRACKED_COOKIES = ['_fbp', '_fbc'] as const;

export interface Attribution {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
    gclid?: string;
    fbclid?: string;
    ttclid?: string;
    msclkid?: string;
    fbp?: string;
    fbc?: string;
    ga_client_id?: string;
    landing_path?: string;
    referrer?: string;
    captured_at?: string;
    expires_at?: string;
}

function readCookie(name: string): string | undefined {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : undefined;
}

function readGaClientId(): string | undefined {
    // GA4 _ga cookie format: GA1.1.<client_id_part>.<timestamp>
    const ga = readCookie('_ga');
    if (!ga) return undefined;
    const parts = ga.split('.');
    if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
    return undefined;
}

export function captureOnce(): Attribution | null {
    if (typeof window === 'undefined') return null;
    const existing = read();
    if (existing && existing.expires_at && new Date(existing.expires_at) > new Date()) {
        return existing;
    }

    const url = new URL(window.location.href);
    const data: Attribution = {};

    for (const key of TRACKED_PARAMS) {
        const v = url.searchParams.get(key);
        if (v) (data as any)[key] = v;
    }

    for (const cookie of TRACKED_COOKIES) {
        const v = readCookie(cookie);
        if (v) (data as any)[cookie.replace(/^_/, '')] = v;
    }

    const gaClientId = readGaClientId();
    if (gaClientId) data.ga_client_id = gaClientId;

    data.landing_path = url.pathname;
    data.referrer = document.referrer || undefined;
    data.captured_at = new Date().toISOString();
    data.expires_at = new Date(Date.now() + TTL_MS).toISOString();

    // Don't persist empty payloads (no params + no cookies + no referrer)
    const hasContent = TRACKED_PARAMS.some(p => (data as any)[p])
                    || TRACKED_COOKIES.some(c => (data as any)[c.replace(/^_/, '')])
                    || data.referrer
                    || data.ga_client_id;
    if (!hasContent) return null;

    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch { /* storage full or disabled */ }
    return data;
}

export function read(): Attribution | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Attribution;
        if (parsed.expires_at && new Date(parsed.expires_at) <= new Date()) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function clear(): void {
    if (typeof window === 'undefined') return;
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* */ }
}
